import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';

/**
 * Custom hook for Gross Revenue by Projects (GRBP) auto-save functionality
 * 
 * This hook manages:
 * - Auto-save detection and restoration
 * - Polling for data changes every 500ms
 * - Manual update flag to prevent polling overwrites
 * - Pending deletes tracking
 * 
 */
export const useGRBPAutoSave = (
    isGRBPEditing: boolean,
    projectsData: any[],
    GRBPRef: React.RefObject<any>,
    setProjectsData: (data: any) => void,
    expandAllCategoriesWithChildren?: (data: any[]) => void
) => {
    // Auto-save state
    const [GRBPAutoSaveVisible, setGRBPAutoSaveVisible] = useState(false);
    const [GRBPEditData, setGRBPEditData] = useState<any[]>([]);
    const [GRBPPendingDeletes, setGRBPPendingDeletes] = useState<any[]>([]);
    const isGRBPManualUpdateRef = useRef(false);

    // Auto-save: construct unique pageKey for Gross Revenue by Projects
    const GRBPPageKey = useMemo(() => {
        return EDIT_PAGE_KEY_MAP.publishing_overview_gross_revenue;
    }, []);

    // Auto-save: get methods (check if auto-save exists) - First call without dependencies
    const GRBPAutoSaveMethods: any = useAutoSaveParams({
        pageKey: GRBPPageKey,
    });

    const {
        getSaveParamsValue: getGRBPSaveParamsValue,
        showRestorePrompt: showGRBPRestorePrompt,
        deleteSaveParamsByPageKey: deleteGRBPSaveParamsByPageKey
    } = GRBPAutoSaveMethods;

    // Track edit data changes for GRBP auto-save - Poll frequently to capture real-time edits
    useEffect(() => {
        if (isGRBPEditing && GRBPRef.current) {
            // Initialize edit data when entering edit mode
            if (projectsData.length > 0) {
                setGRBPEditData(projectsData);
            }

            const interval = setInterval(() => {
                // Skip polling if we just did a manual update (like deletion/reorder)
                if (isGRBPManualUpdateRef.current) {
                    isGRBPManualUpdateRef.current = false;
                    return;
                }

                const currentEditData = GRBPRef.current?.getEditData?.();
                if (currentEditData && Array.isArray(currentEditData)) {
                    setGRBPEditData((prev: any[]) => {
                        const prevStr = JSON.stringify(prev);
                        const currentStr = JSON.stringify(currentEditData);
                        if (prevStr !== currentStr) {
                            return currentEditData;
                        }
                        return prev;
                    });
                }
            }, 500); // Check every 500ms for changes

            return () => clearInterval(interval);
        } else if (!isGRBPEditing) {
            // Reset when exiting edit mode
            setGRBPEditData([]);
            GRBPInitialParamsRef.current = {
                tableData: [],
                pendingDeletes: [],
            };
        }
    }, [isGRBPEditing, projectsData, GRBPRef]);

    // 🔥 CRITICAL: Only compute GRBP currentSaveParams when in edit mode to prevent race conditions
    // Only save actual data changes (tableData and pendingDeletes), NOT UI state like expandedKeys
    const GRBPCurrentSaveParams = useMemo(() => {
        if (!isGRBPEditing) {
            // When not editing, return empty params to prevent auto-save from triggering
            return {
                tableData: [],
                pendingDeletes: [],
            };
        }

        // When editing, use actual edit data
        return {
            tableData: GRBPEditData.length > 0 ? GRBPEditData : projectsData,
            pendingDeletes: GRBPPendingDeletes,
        };
    }, [isGRBPEditing, GRBPEditData, GRBPPendingDeletes, projectsData]);

    // 🔥 CRITICAL: Track initial params to detect real changes
    const GRBPInitialParamsRef = useRef({
        tableData: [],
        pendingDeletes: [],
    });

    // Initialize ref when entering edit mode with actual data
    useEffect(() => {
        if (isGRBPEditing && projectsData.length > 0 && GRBPInitialParamsRef.current.tableData.length === 0) {
            GRBPInitialParamsRef.current = {
                tableData: JSON.parse(JSON.stringify(projectsData)),
                pendingDeletes: [],
            };
        }
    }, [isGRBPEditing, projectsData]);

    // Start auto-saving GRBP when in edit mode
    useAutoSaveParams({
        pageKey: GRBPPageKey,
        initialParams: GRBPInitialParamsRef.current,
        saveParamsCur: GRBPCurrentSaveParams,
        isBegin: isGRBPEditing,
        isChangeValue: true,
    });

    // Handler: Check for auto-save before entering edit mode
    const handleGRBPEdit = useCallback(() => {
        if (showGRBPRestorePrompt(GRBPPageKey)) {
            setGRBPAutoSaveVisible(true);
        } else {
            GRBPRef.current?.handleEdit();
        }
    }, [showGRBPRestorePrompt, GRBPPageKey, GRBPRef]);

    // Handler: Discard auto-save and enter edit mode
    const handleGRBPAutoSaveCancel = useCallback(() => {
        setGRBPAutoSaveVisible(false);
        deleteGRBPSaveParamsByPageKey(GRBPPageKey);
        setGRBPPendingDeletes([]);
        GRBPRef.current?.handleEdit();
    }, [deleteGRBPSaveParamsByPageKey, GRBPPageKey, GRBPRef]);

    // Handler: Restore from auto-save
    const handleGRBPAutoSaveConfirm = useCallback(() => {
        const saveValue = getGRBPSaveParamsValue(GRBPPageKey) || {};

        console.log('🔄 [GRBP AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

        setGRBPAutoSaveVisible(false);

        // Restore pending deletes if they exist
        if (saveValue.pendingDeletes && Array.isArray(saveValue.pendingDeletes)) {
            setGRBPPendingDeletes(saveValue.pendingDeletes);
        }

        // 🔥 CRITICAL: Update projectsData with saved data
        if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0) {
            setProjectsData(saveValue.tableData);
            // 🔥 CRITICAL: Do NOT update originalProjectsData here!
            // originalProjectsData should remain the API's original data for comparison
        }

        // 🔥 CRITICAL: Set manual update flag to prevent polling from overwriting restored data
        isGRBPManualUpdateRef.current = true;

        // Update edit data state for auto-save tracking
        if (saveValue.tableData && Array.isArray(saveValue.tableData)) {
            setGRBPEditData(saveValue.tableData);
        }

        // Enter edit mode after state updates
        setTimeout(() => {
            GRBPRef.current?.handleEdit();
            // Expand categories after restoring data from auto-save
            // Use the restored data directly to ensure expansion happens
            if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0 && expandAllCategoriesWithChildren) {
                expandAllCategoriesWithChildren(saveValue.tableData);
            }
        }, 100);
    }, [getGRBPSaveParamsValue, GRBPPageKey, GRBPRef, setProjectsData, expandAllCategoriesWithChildren]);

    // Clear auto-save after successful save
    const clearGRBPAutoSave = useCallback(() => {
        deleteGRBPSaveParamsByPageKey(GRBPPageKey);
        setGRBPPendingDeletes([]);
    }, [deleteGRBPSaveParamsByPageKey, GRBPPageKey]);

    // Check if there are unsaved changes (for RoutePrompt)
    const hasGRBPUnsavedChanges = useMemo(() => {
        if (!isGRBPEditing) return false;
        
        const initialData = GRBPInitialParamsRef.current?.tableData;
        if (!initialData || initialData.length === 0) return false;
        
        const currentData = GRBPEditData.length > 0 ? GRBPEditData : projectsData;
        
        // Compare using JSON stringify
        const hasChanges = JSON.stringify(initialData) !== JSON.stringify(currentData);
        
        return hasChanges;
    }, [isGRBPEditing, GRBPEditData, projectsData]);

    return {
        // State
        GRBPAutoSaveVisible,
        GRBPEditData,
        GRBPPendingDeletes,
        GRBPPageKey,
        hasGRBPUnsavedChanges,

        // State setters (for external updates)
        setGRBPEditData,
        setGRBPPendingDeletes,

        // Handlers
        handleGRBPEdit,
        handleGRBPAutoSaveCancel,
        handleGRBPAutoSaveConfirm,
        clearGRBPAutoSave,
    };
};
